import { TreeNode, TreeNodeProps } from "@/components/ui/tree/tree-node";
import { selectSelectedEntitiesRecursive } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { selectIsEntitySetToVisible } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-visibility-selectors";
import { selectPointCloudStreamForScanEntity } from "@/data-preparation-tool/store/revision/revision-scan-selectors";
import { useDelayedDeactivation } from "@/hooks/use-delayed-deactivation";
import { RootState } from "@/store/store";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  CircularProgress,
  FaroIconButton,
  FaroIconButtonSizes,
  NonVisibleIcon,
  TruncatedFaroTextWithSuffix,
  useBreakpointMdUp,
  VisibleIcon,
} from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  isRevisionScanEntity,
} from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import { Stack, useMediaQuery } from "@mui/system";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import type { NodeRendererProps } from "react-arborist";
import {
  setHoveredEntityId,
  unsetHoveredEntityId,
} from "../../store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectRevisionEntity,
  selectRevisionEntityChildrenMap,
} from "../../store/revision/revision-entity-selectors";
import { PREVIEW_CLOSE_DELAY_MS, ScanPreview } from "../scan-preview";
import { EntityIcon } from "./entity-icon";
import { ScanTreeContextMenu } from "./scan-tree-context-menu";
import { toggleVisibility } from "./scan-tree-context-menu-action-items/visibility-toggle-context-menu-action-item";
import { ScanTreeNodeHoverContext } from "./scan-tree-node-hover-context";

type ScanTreeNodeProps = TreeNodeProps<CaptureTreeEntityRevision> &
  Pick<
    NodeRendererProps<CaptureTreeEntityRevision>,
    "dragHandle" | "preview" | "tree"
  > & {
    showContextMenu: boolean;
    hierarchySaveTargetId?: GUID;
    isSavingHierarchyChanges?: boolean;
    onExpandNode?(entityId: GUID): void;
  };

/** @returns a node to be shown in the ScanTree */
export function ScanTreeNode({
  node,
  style,
  dragHandle,
  preview,
  tree,
  showContextMenu,
  hierarchySaveTargetId,
  isSavingHierarchyChanges,
  onExpandNode,
}: ScanTreeNodeProps): JSX.Element {
  const { dispatch, getState } = useAppStore();
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const selectedEntities = useAppSelector(selectSelectedEntitiesRecursive);
  const isSelectedRecursive = useMemo(
    () => selectedEntities.some((entity) => entity.id === node.id),
    [selectedEntities, node],
  );
  const isHierarchySaveTarget =
    isSavingHierarchyChanges && hierarchySaveTargetId === node.id;
  const isLargeScreen = useBreakpointMdUp();
  const supportsHovering = useMediaQuery("(hover: hover)");
  const [isNodeHovered, setIsNodeHovered] = useState(false);
  // Unset the global hover state if the component unmounts without a mouse event
  useEffect(
    () => () => {
      dispatch(unsetHoveredEntityId(node.id));
    },
    [dispatch, node.id],
  );

  const disabledReason = useAppSelector(selectScanNodeDisabledReason(node.id));
  const isDisabled = !!disabledReason;
  const childrenMap = useAppSelector(selectRevisionEntityChildrenMap);

  // Since childrenAccessor in ScanTree returns an empty array for entities that can have children
  // but currently don't, it's necessary to check if the entity actually has any children.
  const isLeaf = node.isLeaf || !childrenMap[node.id]?.length;

  const isEntitySetToVisible = useAppSelector(
    selectIsEntitySetToVisible(node.id),
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isContextMenuOpened, setIsContextMenuOpened] = useState(false);
  // Whether the pointer is currently over the preview popover itself. This keeps
  // the preview open (and interactive) while the user hovers it, even though the
  // tree node is no longer hovered.
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);

  // Get the hover context to broadcast hover state to sibling nodes.
  const hoverContext = useContext(ScanTreeNodeHoverContext);

  // Whether a sibling node in the same tree is currently being hovered.
  const isAnotherNodeHovered =
    hoverContext?.hoveredNodeId !== null &&
    hoverContext?.hoveredNodeId !== node.id;

  // Keep the preview visible while either the node or the preview is hovered.
  // The delayed deactivation bridges the gap as the pointer moves from the node
  // onto the preview, so it doesn't flicker closed in between.
  // If a sibling node is being hovered, bypass the delay and close immediately.
  const isPreviewVisible =
    useDelayedDeactivation(
      (isNodeHovered || isPreviewHovered) && !isContextMenuOpened,
      PREVIEW_CLOSE_DELAY_MS,
    ) && !isAnotherNodeHovered;

  const onToggleContextMenu = useCallback(
    (open: boolean) => {
      if (isNodeHovered && open) {
        setIsContextMenuOpened(open);
      }
    },
    [isNodeHovered, setIsContextMenuOpened],
  );

  const contextMenuCleanupAfterActionPerformed = useCallback(() => {
    setIsContextMenuOpened(false);
    hoverContext?.setHoveredNodeId(null);
    setIsNodeHovered(false);
    dispatch(unsetHoveredEntityId(node.id));
  }, [hoverContext, dispatch, node.id]);

  const handlersRef = useCallback(
    (el: HTMLDivElement | null) => {
      setAnchorEl(el);
      dragHandle?.(el);
    },
    [dragHandle],
  );

  // react-arborist renders the drag preview inside a fixed, full-screen-width overlay.
  // Without an explicit width, this node's `width: 100%` content would stretch across the
  // whole screen. Constrain the preview to the actual tree width so it matches a normal row.
  const width = preview
    ? (tree.listEl.current?.clientWidth ?? undefined)
    : undefined;

  return (
    <Box component="div" style={style} ref={handlersRef} sx={{ width }}>
      <TreeNode<CaptureTreeEntityRevision>
        node={node}
        isSelectedIndirectly={isSelectedRecursive}
        shouldExpandNodeOnClick={false}
        shouldDeselectOnClick
        onPointerEnter={() => {
          dispatch(setHoveredEntityId(node.id));
          hoverContext?.setHoveredNodeId(node.id);
          setIsNodeHovered(true);
        }}
        onPointerLeave={() => {
          dispatch(unsetHoveredEntityId(node.id));
          hoverContext?.setHoveredNodeId(null);
          setIsNodeHovered(false);
        }}
        isDisabled={isDisabled}
        isSelectable={!isDisabled}
        style={style}
        isLeafOverride={isLeaf}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyItems: "space-between",
            gap: 1,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            {isHierarchySaveTarget ? (
              <CircularProgress
                aria-label="hierarchy save loading spinner"
                size="1.125em"
              />
            ) : (
              <EntityIcon entity={node.data} sx={{ fontSize: "1.125rem" }} />
            )}
            <TruncatedFaroTextWithSuffix
              variant="bodyM"
              color="inherit"
              containerSx={{ flexGrow: 1, width: "100%" }}
              lengthOfSuffix={3}
              text={node.data.name}
              // the scan name is already shown in the ScanPreview on hover
              showTooltip={false}
              shouldTranslate={false}
            />
          </Stack>

          <FaroIconButton
            size={FaroIconButtonSizes.xs}
            onClick={(ev) => {
              toggleVisibility(
                dispatch,
                getState(),
                isEntitySetToVisible,
                node.data,
              );

              // Don't deselect the entity when toggling visibility
              ev.stopPropagation();
            }}
            sx={{
              color: "inherit",
              "& > svg": { color: "inherit !important" },
            }}
          >
            <>
              {isEntitySetToVisible &&
                supportsHovering &&
                (isNodeHovered || isSelectedRecursive) && <VisibleIcon />}
              {!isEntitySetToVisible && <NonVisibleIcon />}
            </>
          </FaroIconButton>

          {showContextMenu && hasWritePermission && (
            <ScanTreeContextMenu
              entity={node.data}
              disabled={isDisabled}
              visible={isNodeHovered || isSelectedRecursive}
              onToggle={onToggleContextMenu}
              onCloseContextMenu={contextMenuCleanupAfterActionPerformed}
              onExpandNode={onExpandNode}
            />
          )}
        </Stack>
      </TreeNode>
      {/* On smaller screens the sidebar is in a drawer and the preview wouldn't be visible */}
      {isLargeScreen && (
        <ScanPreview
          scanId={node.id}
          isHovering={isNodeHovered && !isContextMenuOpened}
          disablePortal={false}
          isVisible={isPreviewVisible}
          onPreviewHoverChange={setIsPreviewHovered}
          anchorEl={anchorEl}
          placement="right"
        />
      )}
    </Box>
  );
}

/**
 * @returns a reason for disabling a node in the scan tree, or undefined if the nodes should be enabled
 * @param nodeId the id of the entity for the node
 */
function selectScanNodeDisabledReason(nodeId: GUID) {
  return (state: RootState): string | undefined => {
    const nodeEntity = selectRevisionEntity(nodeId)(state);

    if (!nodeEntity) return;

    if (
      isRevisionScanEntity(nodeEntity) &&
      !selectPointCloudStreamForScanEntity(nodeEntity)(state)
    ) {
      return "This point cloud is currently not available";
    }
  };
}
